import { useState, useMemo } from 'react'
import { format } from 'date-fns'
import { MessageSquare, Upload, Clock, TrendingUp, Filter, CheckCircle } from 'lucide-react'
import { useUserAnalytics, AnalyticsDateRange } from '@/hooks/useAnalytics'
import { StatCard } from './StatCard'
import { cn } from '@/lib/utils'

interface UserAnalyticsProps {
  className?: string
}

export function UserAnalytics({ className }: UserAnalyticsProps) {
  const [dateRange, setDateRange] = useState<AnalyticsDateRange>({})

  // Default date range: last 30 days
  const defaultDateRange = useMemo(() => {
    const endDate = new Date()
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - 30)

    return {
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(endDate, 'yyyy-MM-dd')
    }
  }, [])

  const { data: analytics, loading, error, refetch } = useUserAnalytics(
    Object.keys(dateRange).length > 0 ? dateRange : defaultDateRange
  )

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateRange(prev => ({ ...prev, startDate: e.target.value }))
  }

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateRange(prev => ({ ...prev, endDate: e.target.value }))
  }

  const handleResetFilter = () => {
    setDateRange({})
  }

  // Format last activity timestamp
  const formatLastActivity = (timestamp: string | null) => {
    if (!timestamp) return 'No activity yet'

    try {
      const date = new Date(timestamp)
      const now = new Date()
      const diffMs = now.getTime() - date.getTime()
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)

      if (diffMins < 60) {
        return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`
      } else if (diffHours < 24) {
        return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`
      } else if (diffDays < 7) {
        return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`
      } else {
        return format(date, 'MMM d, yyyy')
      }
    } catch {
      return 'Invalid date'
    }
  }

  // Mock login history data (replace with real API when available)
  const loginHistory = [
    { id: 1, timestamp: new Date(Date.now() - 1000 * 60 * 5), ip: '192.168.1.100', location: 'New York, US' },
    { id: 2, timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2), ip: '192.168.1.100', location: 'New York, US' },
    { id: 3, timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24), ip: '192.168.1.105', location: 'New York, US' },
    { id: 4, timestamp: new Date(Date.now() - 1000 * 60 * 60 * 48), ip: '192.168.1.100', location: 'New York, US' },
    { id: 5, timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24 * 7), ip: '10.0.0.50', location: 'Boston, US' },
  ]

  // Mock recent activity data (replace with real API when available)
  const recentActivity = [
    {
      id: 1,
      type: 'chat',
      description: 'Sent 3 chat messages',
      timestamp: new Date(Date.now() - 1000 * 60 * 15),
      icon: MessageSquare
    },
    {
      id: 2,
      type: 'upload',
      description: 'Viewed presentation "AI Workshop Slides"',
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 3),
      icon: CheckCircle
    },
    {
      id: 3,
      type: 'login',
      description: 'Logged in to the platform',
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 5),
      icon: Clock
    },
    {
      id: 4,
      type: 'upload',
      description: 'Viewed PDF document "Company Guidelines"',
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24),
      icon: CheckCircle
    },
    {
      id: 5,
      type: 'chat',
      description: 'Started a new conversation',
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 48),
      icon: MessageSquare
    },
  ]

  // Loading state
  if (loading) {
    return (
      <div className={cn("space-y-6", className)}>
        {/* Date Range Filter Skeleton */}
        <div className="bg-card border border-border p-4 rounded-lg">
          <div className="flex items-center gap-4">
            <div className="h-10 bg-muted rounded animate-pulse w-32"></div>
            <div className="h-9 bg-muted rounded animate-pulse w-40"></div>
            <div className="h-9 bg-muted rounded animate-pulse w-40"></div>
          </div>
        </div>

        {/* Stats Cards Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-card border border-border p-6 rounded-lg animate-pulse">
              <div className="flex items-center justify-between mb-4">
                <div className="h-4 bg-muted rounded w-24"></div>
                <div className="h-8 bg-muted rounded w-8"></div>
              </div>
              <div className="h-8 bg-muted rounded w-16 mb-2"></div>
              <div className="h-3 bg-muted rounded w-32"></div>
            </div>
          ))}
        </div>

        {/* Login History Skeleton */}
        <div className="bg-card border border-border p-6 rounded-lg">
          <div className="h-6 bg-muted rounded w-32 mb-4 animate-pulse"></div>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-16 bg-muted/50 rounded animate-pulse"></div>
            ))}
          </div>
        </div>

        {/* Recent Activity Skeleton */}
        <div className="bg-card border border-border p-6 rounded-lg">
          <div className="h-6 bg-muted rounded w-40 mb-4 animate-pulse"></div>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-16 bg-muted/50 rounded animate-pulse"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className={cn("bg-destructive/10 border border-destructive/20 p-6 rounded-lg", className)}>
        <div className="flex items-start gap-4">
          <div className="p-2 bg-destructive/20 rounded-lg">
            <TrendingUp className="w-5 h-5 text-destructive" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground mb-2">Failed to Load Analytics</h3>
            <p className="text-muted-foreground mb-4">{error}</p>
            <button
              onClick={() => refetch()}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={cn("space-y-6", className)}>
      {/* Date Range Filter */}
      <div className="bg-card border border-border p-4 rounded-lg">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Filter className="w-4 h-4" />
            <span>Date Range:</span>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <label htmlFor="start-date" className="text-sm text-muted-foreground">
                From:
              </label>
              <input
                id="start-date"
                type="date"
                value={dateRange.startDate || defaultDateRange.startDate}
                onChange={handleStartDateChange}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm text-foreground shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>

            <div className="flex items-center gap-2">
              <label htmlFor="end-date" className="text-sm text-muted-foreground">
                To:
              </label>
              <input
                id="end-date"
                type="date"
                value={dateRange.endDate || defaultDateRange.endDate}
                onChange={handleEndDateChange}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm text-foreground shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>

            {(dateRange.startDate || dateRange.endDate) && (
              <button
                onClick={handleResetFilter}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-md hover:bg-muted"
              >
                Reset
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Personal Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          title="Chat Messages"
          value={analytics?.chatMessages || 0}
          icon={MessageSquare}
          description="Total messages sent"
          className="cursor-pointer hover:border-primary/50 transition-colors"
        />

        <StatCard
          title="Content Uploads"
          value={analytics?.contentUploads || 0}
          icon={Upload}
          description="Files uploaded to library"
          className="cursor-pointer hover:border-primary/50 transition-colors"
        />

        <StatCard
          title="Last Activity"
          value={formatLastActivity(analytics?.lastActivity || null)}
          icon={Clock}
          description="Most recent platform activity"
          className="cursor-pointer hover:border-primary/50 transition-colors"
        />
      </div>

      {/* Login History */}
      <div className="bg-card border border-border p-6 rounded-lg">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Login History</h3>
          <span className="text-sm text-muted-foreground">Recent 5 logins</span>
        </div>

        <div className="space-y-3">
          {loginHistory.map((login, index) => (
            <div
              key={login.id}
              className={cn(
                "flex items-center gap-4 p-3 rounded-lg transition-colors",
                index === 0 ? "bg-primary/5 border border-primary/10" : "bg-muted/30"
              )}
            >
              <div className={cn(
                "p-2 rounded-lg",
                index === 0 ? "bg-primary/10" : "bg-muted"
              )}>
                <Clock className={cn(
                  "w-4 h-4",
                  index === 0 ? "text-primary" : "text-muted-foreground"
                )} />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm font-medium text-foreground">
                    {index === 0 ? 'Current session' : 'Previous login'}
                  </p>
                  {index === 0 && (
                    <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary rounded-full">
                      Active
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                  <span>{format(login.timestamp, 'MMM d, yyyy • h:mm a')}</span>
                  <span>•</span>
                  <span>IP: {login.ip}</span>
                  <span>•</span>
                  <span>{login.location}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Activity Timeline */}
      <div className="bg-card border border-border p-6 rounded-lg">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Recent Activity</h3>
          <span className="text-sm text-muted-foreground">Latest activities</span>
        </div>

        <div className="space-y-3">
          {recentActivity.map((activity) => {
            const Icon = activity.icon

            return (
              <div
                key={activity.id}
                className="flex items-start gap-4 p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
              >
                <div className="p-2 bg-muted rounded-lg flex-shrink-0">
                  <Icon className="w-4 h-4 text-muted-foreground" />
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground mb-1">
                    {activity.description}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {format(activity.timestamp, 'MMM d, yyyy • h:mm a')}
                  </p>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
